import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Calculator, 
  CheckCircle2, 
  Clock, 
  FileCheck, 
  HandshakeIcon, 
  ShieldCheck,
  ChevronRight,
  DollarSign,
  Percent,
  Calendar
} from 'lucide-react';

const FinancingPage = () => {
  const { t } = useTranslation();
  const [calculator, setCalculator] = useState({
    price: 25000,
    downPayment: 5000,
    rate: 6.99,
    term: 60,
  });

  const calculatePayment = () => {
    const principal = calculator.price - calculator.downPayment;
    const monthlyRate = calculator.rate / 100 / 12;
    const numPayments = calculator.term;
    
    if (monthlyRate === 0) {
      return (principal / numPayments).toFixed(2);
    }
    
    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
    return payment.toFixed(2);
  };

  const steps = [
    { icon: FileCheck, title: t('financing.step1Title'), description: t('financing.step1Desc') },
    { icon: Clock, title: t('financing.step2Title'), description: t('financing.step2Desc') },
    { icon: HandshakeIcon, title: t('financing.step3Title'), description: t('financing.step3Desc') },
    { icon: CheckCircle2, title: t('financing.step4Title'), description: t('financing.step4Desc') },
  ];

  const benefits = [
    t('financing.quickApproval'),
    t('financing.competitiveRates'),
    t('financing.noHiddenFees'),
    t('financing.allSituations'),
    t('financing.personalSupport'),
    t('financing.flexiblePayments'),
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="financing-page">
      {/* Hero Section with Navbar */}
      <div className="relative bg-slate-900 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/164634/pexels-photo-164634.jpeg?w=1920" 
            alt="" 
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-slate-900/50" />
        </div>
        
        {/* Navbar - white background */}
        <div className="relative z-10">
          <Navbar />
        </div>
        
        {/* Hero Content */}
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-1 w-12 bg-[#3b82f6] rounded-full"></div>
                <span className="text-[#3b82f6] font-semibold text-sm uppercase tracking-wider">{t('financing.subtitle')}</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {t('financing.heroTitle').split(' ').slice(0, -1).join(' ')} <span className="text-[#3b82f6]">{t('financing.heroTitle').split(' ').pop()}</span>
              </h1>
              <p className="text-slate-300 text-lg md:text-xl mb-8 leading-relaxed">
                {t('financing.heroSubtitle')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-[#3b82f6] hover:bg-blue-600 rounded-full font-semibold px-8 py-6 text-base">
                  <Link to="/credit">
                    {t('financing.applyNow')}
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full font-semibold px-8 py-6 text-base">
                  <a href="tel:+18339956477">
                    {t('financing.callUs')}
                  </a>
                </Button>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-[#3b82f6] p-3 rounded-xl">
                    <ShieldCheck className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">{t('financing.ourAdvantages')}</h3>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  {benefits.map((benefit) => (
                    <div key={benefit} className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-400 flex-shrink-0" />
                      <span className="text-slate-200 text-base">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Calculator Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('financing.calculator')}
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              {t('financing.calculatorSubtitle')}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                      <DollarSign className="h-4 w-4 text-[#3b82f6]" />
                      {t('financing.vehiclePrice')}
                    </Label>
                    <Input
                      type="number"
                      value={calculator.price}
                      onChange={(e) => setCalculator({...calculator, price: Number(e.target.value)})}
                      className="text-lg"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                      <DollarSign className="h-4 w-4 text-[#3b82f6]" />
                      {t('financing.downPayment')}
                    </Label>
                    <Input
                      type="number"
                      value={calculator.downPayment}
                      onChange={(e) => setCalculator({...calculator, downPayment: Number(e.target.value)})}
                      className="text-lg"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                      <Percent className="h-4 w-4 text-[#3b82f6]" />
                      {t('financing.interestRate')}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={calculator.rate}
                      onChange={(e) => setCalculator({...calculator, rate: Number(e.target.value)})}
                      className="text-lg"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                      <Calendar className="h-4 w-4 text-[#3b82f6]" />
                      {t('financing.term')}
                    </Label>
                    <select
                      value={calculator.term}
                      onChange={(e) => setCalculator({...calculator, term: Number(e.target.value)})}
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-white text-lg focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/20 focus:border-[#3b82f6]"
                    >
                      <option value={36}>36 {t('financing.months')}</option>
                      <option value={48}>48 {t('financing.months')}</option>
                      <option value={60}>60 {t('financing.months')}</option>
                      <option value={72}>72 {t('financing.months')}</option>
                      <option value={84}>84 {t('financing.months')}</option>
                    </select>
                  </div>
                </div>

                <div className="flex flex-col justify-center items-center bg-gradient-to-br from-[#3b82f6] to-blue-600 rounded-xl p-8 text-white">
                  <Calculator className="h-12 w-12 mb-4 opacity-80" />
                  <p className="text-blue-100 mb-2">{t('financing.estimatedPayment')}</p>
                  <p className="text-5xl font-bold mb-2">${calculatePayment()}</p>
                  <p className="text-blue-200 text-sm">{t('financing.perMonth')}</p>
                  <p className="text-blue-200 text-xs mt-4 text-center">
                    {t('financing.calculatorDisclaimer')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('financing.howItWorks')}
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              {t('financing.howItWorksSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={step.title} className="relative">
                <div className="bg-slate-50 rounded-2xl p-6 h-full border border-slate-100 hover:shadow-lg transition-shadow">
                  <div className="bg-[#3b82f6] w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                    <step.icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="absolute -top-3 -right-3 bg-[#3b82f6] text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {step.title}
                  </h3>
                  <p className="text-slate-500 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Partners */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <p className="text-center text-sm text-slate-500 uppercase tracking-[0.15em] mb-10 font-semibold">
            {t('financing.partners')}
          </p>
          <img 
            src="https://res.cloudinary.com/dtqyyb6su/image/upload/v1774460836/entrepot/assets/financing_image.png" 
            alt="Nos partenaires financiers - Desjardins, TD Bank, Scotiabank, iA Financial Group, Eden Park, Location Accès Crédit, Santander, Iceberg Finance"
            className="w-full max-w-5xl mx-auto"
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#3b82f6] to-blue-600">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {t('financing.readyTitle')}
          </h2>
          <p className="text-blue-100 mb-8 text-lg max-w-2xl mx-auto">
            {t('financing.readySubtitle')}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-[#3b82f6] hover:bg-blue-50 rounded-lg font-semibold px-8">
              <Link to="/credit">
                {t('financing.applyNow')}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-lg font-semibold px-8">
              <Link to="/inventory">
                {t('financing.seeInventory')}
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FinancingPage;
